import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { Card, SectionLabel, Pill, DataTable, DecisionSummary } from '../components/ui'
import type { Column } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/rivals/comparison) ────────────────────────────
interface Location {
  id: string
  name: string
}
interface RivalSummary {
  competitor_id: string
  name: string
  status: 'blocked' | 'tracking'
  sku_count: number
  shared: number
  exclusive: number
}
interface CatRival {
  count: number
  overlap: number
}
interface CatRow {
  name: string
  yours: number
  rivals: Record<string, CatRival>
}
interface ExclusiveSku {
  name: string
  brand: string | null
  category: string
  price: number
}
interface ComparisonData {
  location: { id: string; name: string }
  own_menu: boolean
  tracked_rivals: number
  own_sku_total: number
  shared_skus_total: number
  rivals: RivalSummary[]
  categories: CatRow[]
  category_list: string[]
  your_exclusives: ExclusiveSku[]
  top_rival: { competitor_id: string; name: string } | null
  rival_exclusives: ExclusiveSku[]
}

// ─── Formatters (user-local) ─────────────────────────────────────────────────
const fmtMoney = (n: number) => `$${n.toFixed(2)}`
const fmtInt = (n: number) => n.toLocaleString('en-US')

// Deterministic per-rival accent from the CannaSpy palette (presentation only —
// the color carries no data). Assigned by position so a rival keeps one hue.
const RIVAL_COLORS = ['#0bb8b8', '#D396A6', '#5484A4', '#d4900a', '#e05a6a', '#8aafc8']

// Overlap band → chip tone. High share of a catalog overlapping = strong signal.
function overlapTone(pct: number): { bg: string; color: string } {
  if (pct >= 70) return { bg: 'var(--accent-soft)', color: 'var(--accent)' }
  if (pct >= 40) return { bg: 'var(--warm-soft)', color: 'var(--warm)' }
  return { bg: 'var(--rose-soft)', color: 'var(--rose)' }
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function CatalogComparison() {
  const navigate = useNavigate()
  const authFetch = useAuthFetch()

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [data, setData] = useState<ComparisonData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [catFilter, setCatFilter] = useState<string>('All Categories')
  const [locOpen, setLocOpen] = useState(false)
  const [catOpen, setCatOpen] = useState(false)

  // The raw exclusive lists can run to hundreds of rows; open a short slice by
  // default and let the operator expand, so the lists never dominate the page.
  const EX_PREVIEW = 8
  const [showAllYours, setShowAllYours] = useState(false)
  const [showAllRival, setShowAllRival] = useState(false)

  // Load the org's locations, default to the first.
  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((d: { locations?: Location[] }) => {
        const locs = d.locations ?? []
        setLocations(locs)
        if (locs.length > 0) setSelectedLocation((cur) => cur || locs[0].id)
        else setLoading(false)
      })
      .catch(() => setError('Could not load your locations.'))
  }, [authFetch])

  const load = useCallback(() => {
    if (!selectedLocation) return
    setLoading(true); setError(null)
    authFetch(`${API}/api/v1/rivals/comparison?location_id=${selectedLocation}`)
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: ComparisonData }) => { setData(d.data); setCatFilter('All Categories'); setShowAllYours(false); setShowAllRival(false) })
      .catch((e) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [authFetch, selectedLocation])

  useEffect(() => { load() }, [load])

  const selectedName = useMemo(
    () => locations.find((l) => l.id === selectedLocation)?.name ?? data?.location.name ?? '—',
    [locations, selectedLocation, data]
  )

  const rivals = data?.rivals ?? []
  const trackedRivals = data?.tracked_rivals ?? 0
  const ownTotal = data?.own_sku_total ?? 0
  const maxSkus = useMemo(() => Math.max(1, ...rivals.map((r) => r.sku_count)), [rivals])

  // Rival → assigned color, kept stable for the summary cards + category columns.
  const rivalColor = useMemo(() => {
    const m = new Map<string, string>()
    rivals.forEach((r, i) => m.set(r.competitor_id, RIVAL_COLORS[i % RIVAL_COLORS.length]))
    return m
  }, [rivals])

  // Category rows filtered to the active category selection.
  const catRows = useMemo(() => {
    const rows = data?.categories ?? []
    return catFilter === 'All Categories' ? rows : rows.filter((c) => c.name === catFilter)
  }, [data, catFilter])

  const hasComparison = !!data && data.own_menu && trackedRivals > 0 && rivals.length > 0

  const catOptions = ['All Categories', ...(data?.category_list ?? [])]

  // ── Decision: the single strongest supported CURRENT fact ──────────────────
  // "Assortment gap" here means observed catalog breadth a rival lists that isn't
  // matched to the operator's own menu — rival.sku_count − rival.shared. That is
  // rival-only catalog for THIS comparison; it is NOT unique market demand, sales,
  // or a stocking recommendation. The leading category mismatch is the category
  // where that same rival carries the most entries the operator's menu doesn't
  // match (cell.count − cell.overlap). Everything is derived from the response —
  // nothing hardcoded — and clamped at zero so missing/zero cells are safe.
  const decision = useMemo(() => {
    if (!hasComparison || !data) return null
    // Rival whose catalog has the most entries not matched to the operator's menu.
    let topGap: RivalSummary | null = null
    for (const r of rivals) {
      if (r.sku_count <= 0) continue
      const unmatched = Math.max(0, r.sku_count - r.shared)
      const bestUnmatched = topGap ? Math.max(0, topGap.sku_count - topGap.shared) : -1
      if (unmatched > bestUnmatched || (unmatched === bestUnmatched && topGap && r.sku_count > topGap.sku_count)) {
        topGap = r
      }
    }
    if (!topGap || topGap.sku_count - topGap.shared <= 0) return null
    const unmatched = topGap.sku_count - topGap.shared

    // That rival's largest category-level mismatch (count − overlap), if any.
    let leadCat: { name: string; count: number; overlap: number; unmatched: number } | null = null
    for (const c of data.categories) {
      const cell = c.rivals[topGap.competitor_id] ?? { count: 0, overlap: 0 }
      const catUnmatched = Math.max(0, cell.count - cell.overlap)
      if (catUnmatched <= 0) continue
      if (!leadCat || catUnmatched > leadCat.unmatched || (catUnmatched === leadCat.unmatched && cell.count > leadCat.count)) {
        leadCat = { name: c.name, count: cell.count, overlap: cell.overlap, unmatched: catUnmatched }
      }
    }
    return { topGap, unmatched, leadCat }
  }, [hasComparison, data, rivals])

  // ── Dropdown chrome (shared with s22/s23/s25 conventions) ─────────────────
  const ddBtn = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)',
    fontSize: 11, fontWeight: 600, border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
    background: active ? 'var(--accent)' : 'var(--surface)', color: active ? '#fff' : 'var(--text-1)',
    cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
  })
  const ddPop: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 5px)', left: 0, background: 'var(--surface)',
    border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
    zIndex: 300, minWidth: 170, padding: 5, maxHeight: 300, overflowY: 'auto',
  }
  const ddOpt = (sel: boolean): React.CSSProperties => ({
    padding: '7px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
    color: sel ? 'var(--accent)' : 'var(--text-1)', fontWeight: sel ? 700 : 400, whiteSpace: 'nowrap',
  })
  const chevron = (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
  )

  // ── Category table columns (own + one per rival, built dynamically) ─────────
  const catColumns: Column<CatRow>[] = useMemo(() => {
    const cols: Column<CatRow>[] = [
      {
        key: 'category', header: 'Category', mono: false,
        render: (c) => (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>{c.name}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text-3)' }}>You: {fmtInt(c.yours)} SKUs</div>
          </div>
        ),
      },
      {
        key: 'yours', header: `You (${fmtInt(ownTotal)})`, align: 'right',
        render: (c) => <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{fmtInt(c.yours)}</span>,
      },
    ]
    for (const r of rivals) {
      const color = rivalColor.get(r.competitor_id) ?? 'var(--text-2)'
      cols.push({
        key: r.competitor_id,
        header: <span style={{ color }}>{r.name}</span>,
        align: 'left',
        render: (c) => {
          const cell = c.rivals[r.competitor_id] ?? { count: 0, overlap: 0 }
          const barW = Math.round((cell.count / Math.max(1, maxSkus)) * 100)
          const olPct = cell.count > 0 ? Math.round((cell.overlap / cell.count) * 100) : 0
          const ol = overlapTone(olPct)
          return (
            <div style={{ minWidth: 120 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ flex: 1, height: 8, background: 'var(--surface-3)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: 8, borderRadius: 4, width: `${barW}%`, background: color, opacity: 0.75, transition: 'width .5s ease' }} />
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color, width: 24, textAlign: 'right', flexShrink: 0 }}>{fmtInt(cell.count)}</span>
              </div>
              {cell.count > 0 && (
                <div style={{ marginTop: 3 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '2px 7px', borderRadius: 10, fontWeight: 700, background: ol.bg, color: ol.color }}>
                    {fmtInt(cell.overlap)} shared
                  </span>
                </div>
              )}
            </div>
          )
        },
      })
    }
    return cols
  }, [rivals, rivalColor, ownTotal, maxSkus])

  // ── Exclusive SKU row ──────────────────────────────────────────────────────
  const ExRow = ({ s }: { s: ExclusiveSku }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-1)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>{s.category}</div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{fmtMoney(s.price)}</div>
    </div>
  )

  const topRivalColor = data?.top_rival ? (rivalColor.get(data.top_rival.competitor_id) ?? 'var(--slate)') : 'var(--slate)'

  // "Show all / fewer" toggle for the exclusive lists — keeps every row reachable
  // while defaulting to a short slice so the raw lists don't dominate the page.
  const exToggleStyle: React.CSSProperties = {
    marginTop: 8, padding: '5px 10px', borderRadius: 'var(--r-xs)', border: '1px solid var(--border-2)',
    background: 'var(--surface)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--sans)',
    fontSize: 10.5, fontWeight: 700,
  }

  // ── CSV export — the full category matrix (You + each rival's SKUs & shared) ─
  function exportCSV() {
    if (!data) return
    const csvCell = (v: string | number) => {
      const s = String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const header = ['Category', `You (${ownTotal})`, ...rivals.flatMap((r) => [`${r.name} SKUs`, `${r.name} shared`])]
    const lines = data.categories.map((c) =>
      [c.name, c.yours, ...rivals.flatMap((r) => {
        const cell = c.rivals[r.competitor_id] ?? { count: 0, overlap: 0 }
        return [cell.count, cell.overlap]
      })].map(csvCell).join(',')
    )
    const csv = [header.map(csvCell).join(','), ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `catalog-comparison-${selectedName.replace(/\s+/g, '-').toLowerCase()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div
      onClick={() => { setLocOpen(false); setCatOpen(false) }}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)', color: 'var(--text-1)', fontSize: 14, overflow: 'hidden' }}
    >
      {/* ── TOPBAR ── */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0 10px' }}>
          <button
            onClick={() => navigate('/prices')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0 }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
            Price Intelligence
          </button>
          <div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1 }}>
              Catalog Comparison
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 2 }}>
              SKU OVERLAP &amp; GAPS · {selectedName.toUpperCase()}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {hasComparison && (
              <Pill tone="neutral" variant="chip" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {fmtInt(data?.shared_skus_total ?? 0)} shared · {fmtInt(ownTotal)} own SKUs · {fmtInt(trackedRivals)} rival{trackedRivals === 1 ? '' : 's'}
              </Pill>
            )}
            <button
              onClick={exportCSV}
              disabled={!hasComparison}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 'var(--r-sm)', fontSize: 11, fontWeight: 600, cursor: hasComparison ? 'pointer' : 'default', fontFamily: 'var(--sans)', border: '1.5px solid var(--border-2)', background: 'var(--surface-3)', color: 'var(--text-1)', opacity: hasComparison ? 1 : 0.5 }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export
            </button>
          </div>
        </div>
        {/* Sub-nav (Price cluster) */}
        <div style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid var(--border)' }}>
          {[
            { label: 'Price History', to: '/prices/history', active: false },
            { label: 'Catalog Comparison', to: '/prices/catalog', active: true },
            { label: 'Brand Carriers', to: '/prices/brands', active: false },
          ].map((tab) => (
            <div
              key={tab.label}
              onClick={() => navigate(tab.to)}
              style={{ padding: '10px 18px', fontSize: 12, fontWeight: 600, color: tab.active ? 'var(--accent)' : 'var(--text-3)', cursor: 'pointer', borderBottom: tab.active ? '2px solid var(--accent)' : '2px solid transparent', whiteSpace: 'nowrap' }}
            >
              {tab.label}
            </div>
          ))}
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div onClick={(e) => e.stopPropagation()} style={{ padding: '8px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Location</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div style={ddBtn(true)} onClick={(e) => { e.stopPropagation(); setLocOpen((o) => !o); setCatOpen(false) }}>
            {selectedName}{chevron}
          </div>
          {locOpen && (
            <div style={ddPop}>
              {locations.length === 0
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No locations yet</div>
                : locations.map((l) => (
                  <div key={l.id} style={ddOpt(l.id === selectedLocation)}
                    onClick={() => { setSelectedLocation(l.id); setLocOpen(false) }}>
                    {l.name}{l.id === selectedLocation ? ' ✓' : ''}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Category</span>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div data-testid="cat-cat-btn" style={ddBtn(catFilter !== 'All Categories')} onClick={(e) => { e.stopPropagation(); setCatOpen((o) => !o); setLocOpen(false) }}>
            {catFilter}{chevron}
          </div>
          {catOpen && (
            <div data-testid="cat-cat-pop" style={ddPop}>
              {catOptions.length <= 1
                ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No categories yet</div>
                : catOptions.map((opt) => (
                  <div key={opt} style={ddOpt(opt === catFilter)}
                    onClick={() => { setCatFilter(opt); setCatOpen(false) }}>
                    {opt}{opt === catFilter ? ' ✓' : ''}
                  </div>
                ))}
            </div>
          )}
        </div>

        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          {hasComparison
            ? <>Your catalog: <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{fmtInt(data?.shared_skus_total ?? 0)} shared SKUs</span> across {fmtInt(rivals.length)} rival{rivals.length === 1 ? '' : 's'}</>
            : ''}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, minHeight: 0 }}>

        {/* ERROR */}
        {error && (
          <Card variant="sm" style={{ padding: '18px 20px', borderColor: 'var(--danger-soft)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>Couldn't load your catalog comparison.</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginBottom: 12 }}>{error}</div>
            <button onClick={load} style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
          </Card>
        )}

        {/* LOADING */}
        {!error && loading && !data && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 13 }}>
            Pulling your menu and rival catalogs…
          </div>
        )}

        {/* HONEST DEGRADE — no own menu / no tracked rivals */}
        {!error && !loading && data && !hasComparison && (
          <Card variant="sm" style={{ padding: '18px 20px' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Nothing to compare yet</div>
            <div style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 600, marginBottom: 6 }}>
              {!data.own_menu
                ? `Your own menu isn't in range yet for ${selectedName}.`
                : trackedRivals === 0
                  ? `No tracked rivals at ${selectedName} yet.`
                  : `Your tracked rivals have no catalog data at ${selectedName} yet.`}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {!data.own_menu
                ? 'We haven’t collected your own menu for this location, so we can’t line it up against your rivals. Once it’s in range, the side-by-side comparison appears here.'
                : trackedRivals === 0
                  ? 'Add a rival to your tracking set at this location to compare your catalog against theirs.'
                  : 'Your rivals’ menus haven’t been collected yet — check back after the next collection window.'}
            </div>
          </Card>
        )}

        {/* COMPARISON */}
        {!error && hasComparison && data && (
          <>
            {/* DECISION SUMMARY — strongest supported current fact, first viewport */}
            <DecisionSummary
              className="catalog-comparison-summary"
              question="Where does my assortment differ from competitors, and which gap deserves review?"
              priority="watch"
              answer={decision
                ? <>{decision.topGap.name} has the largest observed assortment gap — <strong>{fmtInt(decision.unmatched)}</strong> of its {fmtInt(decision.topGap.sku_count)} catalog entries aren't matched to your menu</>
                : `Your ${fmtInt(trackedRivals)} tracked rival${trackedRivals === 1 ? '' : 's'} at ${selectedName} carry nothing beyond your own menu`}
              impact={decision
                ? (decision.leadCat
                    ? <>{decision.leadCat.name} contributes the largest category-level mismatch: <strong>{fmtInt(decision.leadCat.unmatched)}</strong> of {fmtInt(decision.leadCat.count)} {decision.leadCat.name} entries at {decision.topGap.name} aren't on your menu. This is observed catalog breadth only — it is not a measure of demand, sales, or margin, and a gap is not by itself a reason to stock these items.</>
                    : <>These are catalog entries {decision.topGap.name} lists that your menu doesn't match. This is observed catalog breadth only — it is not a measure of demand, sales, or margin, and a gap is not by itself a reason to stock these items.</>)
                : undefined}
              action={decision ? (
                <button
                  className="btn btn-primary"
                  data-testid="catalog-review-gap"
                  onClick={() => navigate(
                    decision.leadCat
                      ? `/market/sku-gaps?location_id=${encodeURIComponent(data.location.id)}&category=${encodeURIComponent(decision.leadCat.name)}`
                      : `/market/sku-gaps?location_id=${encodeURIComponent(data.location.id)}`
                  )}
                >
                  {decision.leadCat ? `Review ${decision.leadCat.name} gaps in SKU Gap Analysis →` : 'Review gaps in SKU Gap Analysis →'}
                </button>
              ) : undefined}
            >
              <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', margin: '2px 0 0' }}>
                Current catalog snapshot · no comparable prior snapshot is available, so no change over time is shown.
              </p>
            </DecisionSummary>

            {/* RIVAL SUMMARY CARDS */}
            <SectionLabel style={{ color: 'var(--text-3)', margin: '4px 0 8px' }}>Rival catalogs · biggest first</SectionLabel>
            {/* Data-independent auto-fit grid: cards keep a ~216px floor and reflow
                by available width — 5 across at 1440, 4 at ~1024 — so the shared-count
                chips never wrap regardless of how many rivals are tracked. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(216px, 1fr))', gap: 10, marginBottom: 20 }}>
              {rivals.map((r) => {
                const color = rivalColor.get(r.competitor_id) ?? 'var(--text-2)'
                const pctOfYours = ownTotal > 0 ? Math.round((r.shared / ownTotal) * 100) : 0
                const barW = Math.round((r.sku_count / maxSkus) * 100)
                const overlapPct = Math.round((r.shared / Math.max(1, Math.min(r.sku_count, ownTotal || r.sku_count))) * 100)
                const ol = overlapTone(overlapPct)
                return (
                  <Card key={r.competitor_id} variant="sm" style={{ padding: '11px 13px', minWidth: 0 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-1)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                      <Pill variant="chip" tone={r.status === 'blocked' ? 'blocking' : 'tracking'} style={{ flexShrink: 0 }}>{r.status}</Pill>
                    </div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 17, fontWeight: 700, color, lineHeight: 1, margin: '6px 0 4px' }}>{fmtInt(r.sku_count)}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>total SKUs</div>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, marginTop: 8 }}>
                      <div style={{ height: 4, borderRadius: 2, width: `${barW}%`, background: color, opacity: 0.7, transition: 'width .6s ease' }} />
                    </div>
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '2px 7px', borderRadius: 10, fontWeight: 700, background: ol.bg, color: ol.color }}>
                        {fmtInt(r.shared)} shared ({pctOfYours}% of yours)
                      </span>
                    </div>
                  </Card>
                )
              })}
            </div>

            {/* CATEGORY BREAKDOWN */}
            <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h6" /></svg>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>SKU Count by Category</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>Your catalog vs. each rival · shared SKUs highlighted</div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <DataTable
                  columns={catColumns}
                  rows={catRows}
                  rowKey={(c) => c.name}
                  empty={<span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>No categories match this filter.</span>}
                />
              </div>
            </Card>

            {/* EXCLUSIVE SKUS */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--warm)" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Exclusive SKUs</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>What each side carries that the other doesn't</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10, padding: '16px 18px' }}>
                {/* Yours */}
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '12px 14px' }}>
                  <SectionLabel style={{ color: 'var(--accent)', marginBottom: 8 }}>
                    ✓ Your exclusive SKUs (rivals don't carry)
                  </SectionLabel>
                  {data.your_exclusives.length === 0
                    ? <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '6px 0' }}>Every SKU you carry is also on a tracked rival's menu — no exclusives right now.</div>
                    : <>
                        {(showAllYours ? data.your_exclusives : data.your_exclusives.slice(0, EX_PREVIEW)).map((s, i) => <ExRow key={`${s.name}-${i}`} s={s} />)}
                        {data.your_exclusives.length > EX_PREVIEW && (
                          <button onClick={() => setShowAllYours((v) => !v)} style={exToggleStyle}>
                            {showAllYours ? 'Show fewer' : `Show all ${fmtInt(data.your_exclusives.length)}`}
                          </button>
                        )}
                      </>}
                </div>
                {/* Top rival */}
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '12px 14px' }}>
                  <SectionLabel style={{ color: topRivalColor, marginBottom: 8 }}>
                    {data?.top_rival ? `${data.top_rival.name} exclusives (you don't carry)` : 'Rival exclusives'}
                  </SectionLabel>
                  {data.rival_exclusives.length === 0
                    ? <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '6px 0' }}>You already carry everything this rival stocks.</div>
                    : <>
                        {(showAllRival ? data.rival_exclusives : data.rival_exclusives.slice(0, EX_PREVIEW)).map((s, i) => <ExRow key={`${s.name}-${i}`} s={s} />)}
                        {data.rival_exclusives.length > EX_PREVIEW && (
                          <button onClick={() => setShowAllRival((v) => !v)} style={exToggleStyle}>
                            {showAllRival ? 'Show fewer' : `Show all ${fmtInt(data.rival_exclusives.length)}`}
                          </button>
                        )}
                      </>}
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}
